/**
 * src/lib/posts.ts
 * Utilitário para carregar e parsear os posts do blog a partir de arquivos .md.
 * Usa webpack require.context para auto-importar todos os arquivos da pasta content/posts/.
 */

export interface PostMeta {
  slug: string;
  title: string;
  date: string;
  lastEdit?: string;
  author: string;
  tags: string[];
  tldr: string;
  thumbnailUrl: string;
}

export interface Post extends PostMeta {
  content: string;
}

export type PostRecommendationKind = 'recent' | 'related' | 'previous';

export interface PostRecommendation {
  kind: PostRecommendationKind;
  post: Post;
}

function normalizeTag(tag: string): string {
  return tag
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function getMonthKey(dateStr: string): string {
  const [year, month] = dateStr.split('-');
  return year && month ? `${year}-${month}` : '';
}

function hashString(value: string): number {
  let hash = 0;

  for (const char of value) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }

  return hash;
}

/**
 * Parser leve de YAML frontmatter.
 * Suporta strings, valores inline e arrays inline: tags: [a, b, c]
 */
function parseFrontmatter(raw: string): { meta: Partial<PostMeta>; body: string } {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/);
  if (!match) return { meta: {}, body: raw };

  const yamlStr = match[1];
  const body = match[2];
  const meta: Record<string, any> = {};

  for (const line of yamlStr.split('\n')) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;

    const key = line.slice(0, colonIdx).trim();
    const rawValue = line.slice(colonIdx + 1).trim();

    if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
      // Array inline: [tag1, tag2, tag3]
      meta[key] = rawValue
        .slice(1, -1)
        .split(',')
        .map((s) => s.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    } else {
      meta[key] = rawValue.replace(/^['"]|['"]$/g, '');
    }
  }

  return { meta: meta as Partial<PostMeta>, body };
}

type MarkdownContext = {
  (key: string): string;
  keys: () => string[];
};

const emptyMarkdownContext = Object.assign((() => '') as unknown as MarkdownContext, {
  keys: () => [],
});

// webpack require.context — carrega automaticamente todos os .md de content/posts/
let ctx: MarkdownContext = emptyMarkdownContext;

try {
  ctx = (require as any).context('../content/posts', false, /\.md$/);
} catch {
  ctx = emptyMarkdownContext;
}

/** Retorna todos os posts ordenados do mais recente para o mais antigo. */
export function getAllPosts(): Post[] {
  return (ctx.keys() as string[])
    .map((key: string) => {
      // asset/source exporta a string diretamente (sem .default)
      const raw: string = ctx(key);
      const slug = key.replace(/^\.\//, '').replace(/\.md$/, '');
      const { meta, body } = parseFrontmatter(raw);

      return {
        slug,
        title: meta.title ?? '',
        date: meta.date ?? '',
        lastEdit: meta.lastEdit,
        author: meta.author ?? '',
        tags: Array.isArray(meta.tags) ? meta.tags : [],
        tldr: meta.tldr ?? '',
        thumbnailUrl: meta.thumbnailUrl ?? '',
        content: body,
      } as Post;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/** Busca um post pelo slug. Retorna undefined se não encontrado. */
export function getPostBySlug(slug: string): Post | undefined {
  return getAllPosts().find((p) => p.slug === slug);
}

/**
 * Seleciona até 3 recomendações distintas para um post.
 * Ordem dos slots:
 * 1) artigo mais recente/próximo
 * 2) artigo relacionado por tags (ou fallback determinístico)
 * 3) artigo anterior
 */
export function selectRecommendedPosts(posts: Post[], currentSlug: string): PostRecommendation[] {
  const currentIndex = posts.findIndex((post) => post.slug === currentSlug);

  if (currentIndex === -1) return [];

  const currentPost = posts[currentIndex];
  const usedSlugs = new Set([currentPost.slug]);
  const recommendations: PostRecommendation[] = [];
  const newerPosts = posts.slice(0, currentIndex).reverse();
  const olderPosts = posts.slice(currentIndex + 1);

  const addRecommendation = (kind: PostRecommendationKind, candidates: Post[]) => {
    for (const candidate of candidates) {
      if (!candidate || usedSlugs.has(candidate.slug)) continue;

      recommendations.push({ kind, post: candidate });
      usedSlugs.add(candidate.slug);
      return candidate;
    }

    return undefined;
  };

  const sameMonthPostsOldestFirst = posts
    .filter(
      (post) => post.slug !== currentPost.slug && getMonthKey(post.date) === getMonthKey(currentPost.date)
    )
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const recentCandidates =
    currentIndex > 0
      ? [posts[currentIndex - 1], ...newerPosts, ...olderPosts, ...posts.slice().reverse()]
      : [...sameMonthPostsOldestFirst, ...olderPosts, ...posts.slice().reverse()];

  addRecommendation('recent', recentCandidates);

  const currentTags = new Set(currentPost.tags.map(normalizeTag).filter(Boolean));
  const relatedByTags = posts
    .filter((post) => post.slug !== currentPost.slug && !usedSlugs.has(post.slug))
    .map((post) => {
      const normalizedTags = Array.from(new Set(post.tags.map(normalizeTag).filter(Boolean)));
      const sharedTagCount = normalizedTags.filter((tag) => currentTags.has(tag)).length;

      return { post, sharedTagCount };
    })
    .sort(
      (a, b) =>
        b.sharedTagCount - a.sharedTagCount ||
        new Date(b.post.date).getTime() - new Date(a.post.date).getTime() ||
        a.post.slug.localeCompare(b.post.slug)
    );

  const matchingRelatedPosts = relatedByTags
    .filter(({ sharedTagCount }) => sharedTagCount > 0)
    .map(({ post }) => post);

  if (matchingRelatedPosts.length > 0) {
    addRecommendation('related', matchingRelatedPosts);
  } else {
    const fallbackPool = posts.filter((post) => post.slug !== currentPost.slug && !usedSlugs.has(post.slug));

    if (fallbackPool.length > 0) {
      const fallbackIndex = hashString(currentPost.slug) % fallbackPool.length;
      const rotatedPool = [
        fallbackPool[fallbackIndex],
        ...fallbackPool.slice(fallbackIndex + 1),
        ...fallbackPool.slice(0, fallbackIndex),
      ];

      addRecommendation('related', rotatedPool);
    }
  }

  addRecommendation('previous', [...olderPosts, ...newerPosts, ...posts]);

  for (const kind of ['recent', 'related', 'previous'] as PostRecommendationKind[]) {
    if (recommendations.some((recommendation) => recommendation.kind === kind)) continue;
    addRecommendation(kind, posts);
  }

  return recommendations;
}

export function getPostRecommendations(currentSlug: string): PostRecommendation[] {
  return selectRecommendedPosts(getAllPosts(), currentSlug);
}

/** Formata uma data YYYY-MM-DD para português brasileiro: "1 de março de 2026" */
export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

